import { isDefined } from "@omni/utils/helper.utils";
import {
    ControllerFieldState,
    ControllerRenderProps,
    FieldValues,
    Mode,
    Path,
    UseFormStateReturn
} from "react-hook-form";

import { memoizeObject } from "@/utils/memoize";

const isShouldShowState = <TFieldValues extends FieldValues>(
    fieldState: ControllerFieldState,
    formState: UseFormStateReturn<TFieldValues>,
    mode: Mode | undefined
) => {
    if (formState.isSubmitted) {
        return true;
    }

    switch (mode) {
        case "onSubmit":
            return false;
        case "onBlur":
        case "onTouched":
            return fieldState.isTouched;
        case "onChange":
            return fieldState.isDirty;
        case "all":
            return fieldState.isTouched || fieldState.isDirty;
        case undefined:
            return fieldState.isTouched;
    }
};

export const calculateFieldState = <TFieldValues extends FieldValues>(
    fieldState: ControllerFieldState,
    formState: UseFormStateReturn<TFieldValues>,
    mode: Mode | undefined
) => {
    const shouldShowState = isShouldShowState(fieldState, formState, mode);

    return memoizeObject({
        isInvalid: shouldShowState && !!fieldState.error,
        isValid: shouldShowState && !fieldState.error
    });
};

export const getFieldValue = <TFieldValues extends FieldValues, TDefaultValue>(
    field: ControllerRenderProps<
        TFieldValues,
        Extract<Path<TFieldValues>, string>
    >,
    loading: boolean,
    defaultValue: TDefaultValue
) => {
    if (isDefined(field.value)) {
        return field.value;
    } else if (loading) {
        return defaultValue;
    } else {
        return undefined;
    }
};
